import {
  PROFILE_ENVELOPE,
  RUNNER_LEVEL_MILEAGE_POLICY,
  TEN_K_PRODUCT_ENVELOPE,
  normalizeRunningLevel,
  validateRunnerLevelMileage10K,
} from './levels';

// Guardrail for training goals — warns the user (without blocking) when
// target time + weekly volume don't line up with the chosen race distance.
//
// Returns: {
//   status:           'ok' | 'warning' | 'critical',
//   suggestedDistance: string | null,   // e.g. "חצי מרתון", "10K" (null when ok)
//   reason:           string,           // short Hebrew explanation for the modal body
//   weeklyKm:         number | null,    // echoed back for display
//   distance:         string,           // echoed back for display
//   targetTime:       string | null,    // echoed back for display
// }
//
// Rule of thumb:
//   - Warnings exist for the user (injury prevention).
//   - The override flag exists for the AI (risk-awareness when building plans).
//   - Don't block — even CRITICAL ends with a "continue anyway" path.

// Parse "h:mm" / "h:mm:ss" / "mm:ss" into total minutes. Returns null when
// the input doesn't look like a time string.
export function parseTimeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.trim().split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 60 + parts[1] + parts[2] / 60;
  if (parts.length === 2) {
    // Heuristic: a marathon-shaped target like "3:30" is hours:minutes,
    // a 5K-shaped target like "25:00" is minutes:seconds. Anything above
    // 10 in the first slot is almost certainly minutes:seconds (no one
    // runs a 10+ hour race here). Below 10 → hours:minutes.
    if (parts[0] < 10) return parts[0] * 60 + parts[1];
    return parts[0] + parts[1] / 60;
  }
  return null;
}

// Normalise a distance label to one of our canonical buckets.
// Returns null if the distance isn't one of the four supported running races.
// Accepts: '5K', '10K', 'חצי מרתון', 'מרתון', '5', '10', '21.1', '42.2',
// '5km', 'half', 'half marathon', 'marathon', etc.
export function normalizeDistance(distance) {
  if (distance == null) return null;
  const d = String(distance).toLowerCase().trim();
  if (d === '5' || d === '5k' || d === '5 ק"מ' || d === '5km') return '5K';
  if (d === '10' || d === '10k' || d === '10 ק"מ' || d === '10km') return '10K';
  if (d.includes('חצי מרתון') || d.includes('half') || d === '21' || d === '21.1' || d === '21k') return 'חצי מרתון';
  // Marathon must be checked AFTER half marathon because "חצי מרתון"
  // contains the substring "מרתון".
  if (d.includes('מרתון') || d.includes('marathon') || d === '42' || d === '42.2' || d.includes('42')) return 'מרתון';
  return null;
}

// Main validator. All three inputs are nominally required, but the function
// is defensive: missing weeklyKm or targetTime returns 'ok' (we can't say
// anything useful without data).
export function validateUserGoal(weeklyKm, distance, targetTime) {
  const km = typeof weeklyKm === 'number' ? weeklyKm : parseFloat(weeklyKm);
  const dist = normalizeDistance(distance);
  const mins = parseTimeToMinutes(targetTime);

  const base = {
    status: 'ok',
    suggestedDistance: null,
    reason: '',
    weeklyKm: Number.isFinite(km) ? km : null,
    distance: dist,
    targetTime: targetTime || null,
  };

  // No distance or no weekly volume → nothing to validate.
  if (!dist || !Number.isFinite(km) || km <= 0) return base;

  // ── מרתון ─────────────────────────────────────────────────────────────
  if (dist === 'מרתון') {
    // HARD BLOCK: weekly volume under 30 ק"מ for a full marathon is
    // a real injury risk — stress fractures, ITBS, plantar issues are
    // all dramatically more likely on this base. The modal hides the
    // 'continue anyway' button when hardBlock=true; user can ONLY
    // downgrade to half-marathon or close.
    if (km < 30) {
      return {
        ...base,
        status: 'critical',
        hardBlock: true,
        suggestedDistance: 'חצי מרתון',
        reason: `נפח אימון שבועי של ${km} ק"מ נמוך מהותית מהדרישה למרתון (50+ ק"מ). ריצת מרתון על נפח כזה היא סיכון בטיחותי אמיתי — סטרס פרקצ׳ר, ITBS, ופציעות פלנטרית הופכות לסבירות גבוהה. לכן לא נאפשר תכנית למרתון בנפח הזה. ניתן להוריד את היעד לחצי מרתון או לעלות את הנפח השבועי לפני שמכוונים למרתון מלא.`,
      };
    }
    // Time-based checks only if we have a target time. The two buckets
    // produce SAME-SHAPED guidance — required km tied to target pace,
    // not to a separate floor. Bug fix 2026-06-01: previously the
    // 3:30-4:00 bucket said "need 40+", then the catch-all soft
    // guardrail below said "need 50+" for the same athlete once they
    // crossed 40 km. Adding 8 km should NOT raise the bar by another 10.
    if (mins !== null) {
      if (mins < 3 * 60 + 30 && km < 50) {
        return {
          ...base,
          status: 'critical',
          suggestedDistance: 'חצי מרתון',
          reason: `יעד של מתחת ל-3:30 במרתון דורש נפח שבועי של 50+ ק"מ. כרגע אתה על ${km} ק"מ — פער גדול שמעלה סיכון לפציעה.`,
        };
      }
      if (mins >= 3 * 60 + 30 && mins <= 4 * 60) {
        // Pace 4:15-4:45/km — 40+ km/week is the standard floor.
        if (km < 40) {
          return {
            ...base,
            status: 'warning',
            suggestedDistance: null,
            reason: `יעד של ${targetTime} במרתון בדרך כלל דורש 40+ ק"מ שבועי. כרגע אתה על ${km} ק"מ.`,
          };
        }
        // 40-49 km with this target time → we said 40 was enough, honour it.
        return base;
      }
      // > 4:00 target — finish-pace. Falls through to the no-time
      // soft guardrail below (50+ recommended), which matches a
      // relaxed-goal marathon's broader injury-prevention literature.
    }
    // No target time OR target > 4:00: 50+ km is the widely-accepted
    // floor for "can finish without breaking". Only fires when the
    // bucketed time check above didn't already make a call.
    if (km < 50) {
      return {
        ...base,
        status: 'warning',
        suggestedDistance: null,
        reason: `ריצת מרתון מלא מומלצת על בסיס נפח של 50+ ק"מ בשבוע. כרגע אתה על ${km} ק"מ — אפשר להגיע, אבל יש לשקול להעלות נפח בהדרגה לפני שבוע השיא או להוריד את היעד לחצי מרתון.`,
      };
    }
    return base;
  }

  // ── חצי מרתון ────────────────────────────────────────────────────────
  // Tightened spec 2026-06-03 (coach):
  //   • Hard block: km < 20  (matches Marathon < 30 hard-block pattern)
  //   • Warning brackets split into 4 time ranges so users can't slip
  //     through a gap (sub-1:30, sub-1:40, sub-1:45, 1:45-1:50, 1:50-2:00)
  //   • No goal_time edge case: 20≤km<30 → warn that 30+ km is the
  //     baseline for a safe HM finish
  if (dist === 'חצי מרתון') {
    // HARD BLOCK: same shape as the Marathon < 30 km block — the modal
    // hides 'continue anyway' when hardBlock=true and only the
    // 'downgrade to 10K' button is shown.
    if (km < 20) {
      return {
        ...base,
        status: 'critical',
        hardBlock: true,
        suggestedDistance: '10K',
        reason: `נפח שבועי של ${km} ק"מ נמוך מהותית מהדרישה לחצי מרתון. ריצה על בסיס כזה מעלה משמעותית סיכון לפציעה. נדרש לפחות 20 ק"מ בשבוע לחצי מרתון בטוח. ניתן להוריד את היעד ל-10K או לעלות נפח לפני שמכוונים לחצי מרתון.`,
      };
    }
    if (mins !== null) {
      // sub-1:30 (קצב ~4:15/ק"מ) — דורש 55+
      if (mins < 90 && km < 55) {
        return {
          ...base,
          status: 'critical',
          suggestedDistance: null,
          reason: `יעד מתחת ל-1:30 בחצי מרתון (קצב ~4:15/ק"מ) דורש בדרך כלל 55+ ק"מ בשבוע. כרגע אתה על ${km} ק"מ — פער שמעלה משמעותית את סיכון הפציעה ומקטין סיכוי לעמידה ביעד.`,
        };
      }
      // sub-1:40 (קצב ~4:45/ק"מ) — דורש 40+
      if (mins < 100 && km < 40) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `יעד של ${targetTime} (קצב ~4:30-4:40/ק"מ) בדרך כלל דורש 40+ ק"מ בשבוע. כרגע אתה על ${km} ק"מ.`,
        };
      }
      // sub-1:45 — TIGHTENED 2026-06-03: from 30 to 35 km floor
      if (mins < 105 && km < 35) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `יעד מתחת ל-1:45 בחצי מרתון דורש בדרך כלל 35+ ק"מ שבועי. כרגע אתה על ${km} ק"מ.`,
        };
      }
      // 1:45-1:50 — NEW BUCKET 2026-06-03 (split from old 1:45-2:00)
      if (mins >= 105 && mins < 110 && km < 30) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `יעד של ${targetTime} בחצי מרתון דורש בדרך כלל 30+ ק"מ שבועי. כרגע אתה על ${km} ק"מ.`,
        };
      }
      // 1:50-2:00 — TIGHTENED 2026-06-03: from 20 to 25 km floor
      if (mins >= 110 && mins <= 120 && km < 25) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `יעד של ${targetTime} בחצי מרתון בדרך כלל דורש 25+ ק"מ שבועי. כרגע אתה על ${km} ק"מ.`,
        };
      }
    } else {
      // No goal_time specified — coach spec 2026-06-03: HM without a
      // hard target time at 20-29 km/week is still under-volumed
      // enough to warrant a warning. The athlete CAN finish, but
      // 30+ km is the safe baseline for "make it to the line without
      // breaking".
      if (km >= 20 && km < 30) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `מומלץ בסיס נפח של 30+ ק"מ בשבוע לריצת חצי מרתון בטוחה וחלקה. כרגע אתה על ${km} ק"מ — אפשר לסיים, אבל כדאי לעלות נפח לפני המרוץ כדי להוריד סיכון לפציעה.`,
        };
      }
    }
    return base;
  }

  // ── 10K ──────────────────────────────────────────────────────────────
  // Gentle Warning only — 10K is the gateway race; don't scare people away.
  if (dist === '10K') {
    if (km < 10) {
      return {
        ...base,
        status: 'warning',
        suggestedDistance: '5K',
        reason: `נפח של ${km} ק"מ בשבוע הוא בסיס מצומצם ל-10K. אפשר להגיע — כדאי לבנות נפח בהדרגה כדי להוריד סיכון לפציעה.`,
      };
    }
    if (mins !== null) {
      // Tightened thresholds (2026-05-28): sub-40 / sub-45 targets at low
      // mileage were also slipping through.
      if (mins < 40 && km < 40) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `יעד מתחת ל-40 דק׳ ב-10K (קצב ~4:00/ק"מ) דורש בדרך כלל 40+ ק"מ בשבוע. כרגע אתה על ${km} ק"מ.`,
        };
      }
      if (mins < 45 && km < 25) {
        return {
          ...base,
          status: 'warning',
          suggestedDistance: null,
          reason: `יעד מתחת ל-45 דק׳ ב-10K דורש בדרך כלל 25+ ק"מ בשבוע. כרגע אתה על ${km} ק"מ.`,
        };
      }
    }
    return base;
  }

  // 5K — no guardrail.
  return base;
}

// Convenience: build the headline string for the modal title area.
// Returns null for 'ok'.
export function guardrailTitle(status) {
  if (status === 'critical') return 'רגע לפני שיוצאים לדרך... ⚠️';
  if (status === 'warning') return 'המטרה שלך מאתגרת! 🏃‍♂️';
  return null;
}

// ─── Cross-field validation: weekly km × training days ───────────────
// Matrix (per coach spec 2026-06-01):
//   weekly_km ≤ 25   → ≥ 2 days (single runs cap ~12 km)
//   weekly_km 26-45  → ≥ 3 days (single runs cap ~15 km)
//   weekly_km 46-65  → ≥ 4 days (volume distributed: e.g. 10/10/8/22)
//   weekly_km > 65   → ≥ 5 days (marathon load — near-daily runs)
//
// Returns { ok: bool, reason: string|null, requiredDays: number|null }.
// `reason` is the user-facing Hebrew error string to surface inline +
// disable the save button. requiredDays helps the UI render an inline
// hint ("הוסף יום אימון לפחות").
//
// Rationale: 50 km / 2 days = 25 km per session. That's an injury
// machine for an amateur. We refuse to save the combination.
// GENERIC km × days MATRIX (User 2026-08-11). Must stay byte-identical to
// backend/schemas.py :: KM_DAYS_MIN_DAYS — `test_frontend_backend_parity`
// parses this array and fails CI on any divergence.
//
// It replaces two rules that had drifted apart for two months: this file was
// refined on 2026-06-14 (≤35→3, 36-50→4, 51-80→5, 81+→6) while main.py still
// held the 06-01 coach spec (≤25→2, 26-45→3, 46-65→4, >65→5) behind a comment
// claiming it mirrored this one. They disagreed in eleven of eighteen sampled
// volumes, so the client blocked profiles the server accepted and the coach
// endpoint accepted profiles no UI user could create.
//
// Versus the old values here, one relaxation and no tightening: 36-45 km drops
// from four days to three. 40 km over three days is a good week — 10 easy + 12
// quality + 18 long — and nothing about it is structurally unsound. The old
// "45km/3days makes the easy runs longer than the long run" rationale assumed a
// 14 km long; at 18 km it no longer holds.
//
// [weeklyKmInclusiveUpper, minimumDays], ascending.
export const KM_DAYS_MIN_DAYS = [[45, 3], [50, 4], [80, 5], [Infinity, 6]];

export function kmDaysMinDays(weeklyKm) {
  const km = Number(weeklyKm);
  for (const [upper, days] of KM_DAYS_MIN_DAYS) {
    if (km <= upper) return days;
  }
  return KM_DAYS_MIN_DAYS[KM_DAYS_MIN_DAYS.length - 1][1];
}

export function validateKmDaysMatrix(weeklyKm, trainingDays, opts = {}) {
  const km = Number(weeklyKm);
  const days = Number(trainingDays);
  if (!Number.isFinite(km) || km <= 0 || !Number.isFinite(days) || days <= 0) {
    return { ok: true, reason: null, requiredDays: null };
  }
  // Distance-scoped policy (Phase 3): the 10K running flow is governed by the
  // runner-level mileage bands + profile envelope (mirrors backend
  // role_binder.assert_profile_saveable_by_count), NOT the legacy km↔days
  // matrix below. Any caller that passes the goal distance + level opts in;
  // callers that don't (or non-10K distances) keep the legacy behaviour.
  if (_is10kDistance(opts.distance)) {
    return validateProfile10K(opts.level, km, days);
  }
  // TEMPORARY SAFETY PATCH (2026-08-06). The backend adopted a marathon product ceiling of 90 km/week
  // — above it the taper allocator cannot produce a legal week at all — and this file did not know
  // about it, so 91+ km passed here and was rejected on save. This closes that gap ONLY.
  //
  // TODO(marathon-policy): replace this constant, and the km↔days table below, with
  // GET /policy/marathon-availability (schemas.marathon_availability), which already returns
  // supported / minimumTrainingDays / maximumWeeklyKm / reasonCode / userMessage. This patch keeps a
  // duplicated number on purpose — making the validator async touches four call sites on the profile
  // save path and is a separate piece of work. Do not add more policy here; move it instead.
  if (_isMarathonDistance(opts.distance) && km > MARATHON_MAX_WEEKLY_KM) {
    return {
      ok: false,
      requiredDays: null,
      errorCode: 'MARATHON_OVER_MAX_KM',
      reason: `נפח שבועי גבוה מהנתמך למרתון: הזנת ${km} ק"מ בשבוע, והמקסימום הנתמך הוא ${MARATHON_MAX_WEEKLY_KM} ק"מ/שבוע. עדכן את 'נפח שבועי ממוצע' בפרופיל ונסה שוב.`,
      buttonReason: `⚠️ מרתון נתמך עד ${MARATHON_MAX_WEEKLY_KM} ק"מ בשבוע`,
    };
  }
  const required = kmDaysMinDays(km);
  if (days >= required) {
    return { ok: true, reason: null, requiredDays: required };
  }
  return {
    ok: false,
    requiredDays: required,
    // Long form — full sentence for any alert/toast call sites.
    reason: `נפח שבועי של ${km} ק"מ דורש לפחות ${required} ימי אימון בשבוע כדי למנוע פציעות עומס (כרגע ${days} ${days === 1 ? 'יום' : 'ימים'}).`,
    // Short form — fits inside a disabled Save button. The user spec
    // is "ה-disabled state is the explanation": the button itself
    // explains why it's disabled, no extra banner above. Keep this
    // under ~45 chars so it doesn't wrap on the smallest phones.
    buttonReason: `⚠️ נפח כזה דורש לפחות ${required} ימי אימון`,
  };
}

// ─── Distance-scoped 10K profile policy (frontend mirror of backend) ──
// Mirrors role_binder.assert_profile_saveable_by_count for programDistance
// "10K": level→mileage band FIRST, then km×days profile envelope. Any other
// distance is NOT configured yet and must never reuse the 10K policy.
const _HE_LEVEL = { beginner: 'מתחיל', experienced: 'מנוסה', advanced: 'מתקדם' };

// Marathon product ceiling — must equal schemas.MAX_WEEKLY_KM_BY_GOAL['marathon'] on the backend.
// See the TODO in validateKmDaysMatrix: this duplicate exists only until the policy endpoint is wired.
export const MARATHON_MAX_WEEKLY_KM = 90;

export function _isMarathonDistance(distance) {
  const d = String(distance || '').trim().toLowerCase();
  return d === 'marathon' || d === 'מרתון' || d === '42.2' || d === '42k' || d === '42';
}

export function _is10kDistance(distance) {
  const d = String(distance || '').trim().toLowerCase();
  return d === '10k' || d === '10';
}

// The HARD supported mileage span for a day-count, straight from the product
// envelope. This is what eligibility and every refusal message are derived from;
// the recommendation bands never widen or narrow it.
function _supported10kSpan(days) {
  const band = TEN_K_PRODUCT_ENVELOPE[Number(days)];
  return band ? { min: band[0], max: band[1] } : null;
}

// Envelope colour for a 10K profile: 'GREEN' | 'YELLOW' | 'RED' | 'UNSUPPORTED_DAYS'.
//
// RED means INELIGIBLE and is decided by the hard envelope alone. GREEN vs
// YELLOW is advice about comfort within a combination that is already offered,
// so a missing or partial recommendation row can never make a legal profile
// unsaveable — it just stops colouring it.
export function envelopeStatus10K(weeklyKm, trainingDays) {
  const km = Number(weeklyKm);
  const days = Number(trainingDays);
  const hard = TEN_K_PRODUCT_ENVELOPE[days];
  if (!hard) return 'UNSUPPORTED_DAYS';
  if (km < hard[0] || km > hard[1]) return 'RED';
  const row = (PROFILE_ENVELOPE['10K'] || {})[days];
  if (!row) return 'GREEN';
  const within = (band) => band && km >= band[0] && km <= band[1];
  if (within(row.green)) return 'GREEN';
  if (within(row.yellowHi) || within(row.yellowLo)) return 'YELLOW';
  return 'GREEN';
}

// The unified 10K save gate. Returns the SAME shape as validateKmDaysMatrix
// ({ ok, reason, buttonReason, requiredDays }) plus { errorCode, status,
// suggestedLevel } so callers can surface the precise Hebrew guidance and,
// on YELLOW, save-with-recommendation. Save is blocked ONLY on a level↔km
// mismatch or a RED envelope. YELLOW and GREEN are ok:true.
export function validateProfile10K(level, weeklyKm, trainingDays) {
  const km = Math.round(Number(weeklyKm));
  const days = Number(trainingDays);
  if (!Number.isFinite(km) || km <= 0 || !Number.isFinite(days) || days <= 0) {
    return { ok: true, reason: null, buttonReason: null, requiredDays: null, errorCode: null, status: 'GREEN' };
  }
  // 1) runner-level ↔ mileage band (level decides first).
  const rl = validateRunnerLevelMileage10K(level, km);
  if (rl.status === 'INVALID') {
    const lvlHe = _HE_LEVEL[normalizeRunningLevel(level)] || getLevelLabelSafe(level);
    const sugHe = _HE_LEVEL[rl.suggested] || rl.suggested || '';
    const reason = rl.allowedMaxKm === null
      ? `רמת ${lvlHe} מתחילה ב־${rl.allowedMinKm} ק"מ בשבוע. לפי ${km} ק"מ, הרמה המתאימה היא ${sugHe}.`
      : `רמת ${lvlHe} תומכת ב־${rl.allowedMinKm}–${rl.allowedMaxKm} ק"מ בשבוע. לפי ${km} ק"מ, הרמה המתאימה היא ${sugHe}. יש לעדכן את הרמה או להפחית את הנפח.`;
    return {
      ok: false, requiredDays: null, errorCode: 'RUNNER_LEVEL_MILEAGE_MISMATCH',
      status: 'RED', suggestedLevel: rl.suggested, reason,
      buttonReason: `⚠️ הרמה אינה תואמת לנפח — מתאים: ${sugHe}`,
    };
  }
  // 2) profile envelope (km × days).
  const env = envelopeStatus10K(km, days);
  if (env === 'RED' || env === 'UNSUPPORTED_DAYS') {
    const span = _supported10kSpan(days);
    const reason = span
      ? `נפח של ${km} ק"מ בשבוע אינו נתמך עם ${days} ימי אימון (נתמך ${span.min}–${span.max} ק"מ). יש לשנות את מספר ימי האימון או את הנפח השבועי.`
      : `שילוב של ${km} ק"מ עם ${days} ימי אימון אינו נתמך למרחק 10K.`;
    return {
      ok: false, requiredDays: null, errorCode: 'PROFILE_OUT_OF_SUPPORTED_ENVELOPE',
      status: 'RED', reason,
      buttonReason: `⚠️ ${km} ק"מ אינם נתמכים עם ${days} ימי אימון`,
    };
  }
  // GREEN / YELLOW → saveable. YELLOW carries a soft recommendation.
  const row = (PROFILE_ENVELOPE['10K'] || {})[days] || {};
  const recDays = row.recDays;
  return {
    ok: true, requiredDays: null, errorCode: null, status: env,
    reason: env === 'YELLOW' && recDays
      ? `הנפח נתמך, אך לנפח כזה מומלץ ${recDays} ימי אימון בשבוע לחלוקה מאוזנת יותר.`
      : null,
    buttonReason: null,
  };
}

// Local guard so a bad level value never throws inside the validator.
function getLevelLabelSafe(level) {
  const n = normalizeRunningLevel(level);
  return _HE_LEVEL[n] || String(level || '');
}


// ─── Profile save: low-volume WARNING per goal distance ───────────────────────
// Moved verbatim from Profile.jsx saveAllProfiles (2026-10-19) so the save and the weekly-km guidance
// read ONE table. Values unchanged: below these the save asks to confirm (overridable), mirroring
// backend schemas.MIN_WEEKLY_KM_BY_GOAL. 10K is intentionally ABSENT: its floor is governed by the
// runner-level bands + envelope (validateProfile10K).
export const PROFILE_MIN_KM_WARNING = {
  '5k': 20, '5': 20,
  'half_marathon': 35, 'half': 35, 'חצי מרתון': 35, '21.1': 35, '21k': 35, '21': 35,
  'marathon': 50, 'מרתון': 50, '42.2': 50, '42k': 50, '42': 50,
};

// ─── Weekly-km guidance for the Profile field (UI only, 2026-10-19) ────────────
// Describes what the save's km x days check (validateKmDaysMatrix) will do for this distance / day count /
// level. It decides nothing and blocks nothing. The weekly-km MINIMUM (by goal and level) is the plan engine's,
// asked live from the server (GET /policy/running-availability) — no "the save will ask to confirm" any more: there
// is no overridable volume warning since 2026-11-02. Where the rules give one numeric span it says so; where they
// do not (no day count, no level for 10K, a level band that does not meet the day-count envelope) it explains
// instead of inventing a range.
// Returns { kind, min, max, text }:
//   kind 'range'  -> saveable iff min <= km <= max            (10K: level band ∩ day-count envelope)
//   kind 'max'    -> saveable iff km <= max                   (km x days matrix; marathon also <= 90)
//   kind 'open'   -> no upper limit from these rules
//   kind 'none'   -> no weekly volume is saveable with this combination
//   kind 'info'   -> not enough input to describe it yet
export function weeklyKmGuidance({ distance, days, level } = {}) {
  const d = Number(days) || 0;
  if (d <= 0) return { kind: 'info', min: null, max: null, text: 'בחר ימי ריצה כדי לראות את טווח הנפח השבועי.' };

  if (_is10kDistance(distance)) {
    const lvl = normalizeRunningLevel(level);
    const band = (RUNNER_LEVEL_MILEAGE_POLICY['10K'] || {})[lvl];
    if (!band) return { kind: 'info', min: null, max: null, text: 'בחר רמה כדי לראות את טווח הנפח ל-10K.' };
    const span = _supported10kSpan(d);
    const offered = Object.keys(TEN_K_PRODUCT_ENVELOPE).map(Number).sort((a, b) => a - b);
    if (!span) {
      return { kind: 'none', min: null, max: null,
               text: `תכנית 10K נתמכת עם ${offered[0]}–${offered[offered.length - 1]} ימי ריצה בלבד.` };
    }
    const lvlHe = _HE_LEVEL[lvl] || lvl;
    const bandText = band.max === null ? `${band.min}+` : `${band.min}–${band.max}`;
    const lo = Math.max(band.min, span.min);
    const hi = band.max === null ? span.max : Math.min(band.max, span.max);
    if (lo > hi) {
      return { kind: 'none', min: null, max: null,
               text: `אין נפח שמתאים גם לרמת ${lvlHe} (${bandText} ק״מ) וגם ל-${d} ימי ריצה (${span.min}–${span.max} ק״מ). יש לשנות את הרמה או את מספר הימים.` };
    }
    return { kind: 'range', min: lo, max: hi,
             text: lo === hi ? `נפח נתמך: ${lo} ק״מ בשבוע (רמת ${lvlHe}, ${d} ימי ריצה)`
                             : `טווח נתמך: ${lo}–${hi} ק״מ בשבוע (רמת ${lvlHe}, ${d} ימי ריצה)` };
  }

  // km x days matrix: the highest volume this day count may carry, and what the next volume needs.
  const allowed = KM_DAYS_MIN_DAYS.filter(([, need]) => need <= d);
  if (!allowed.length) {
    return { kind: 'none', min: null, max: null,
             text: `נדרשים לפחות ${KM_DAYS_MIN_DAYS[0][1]} ימי ריצה בשבוע.` };
  }
  let max = allowed[allowed.length - 1][0];
  const marathon = _isMarathonDistance(distance);
  if (marathon) max = Math.min(max, MARATHON_MAX_WEEKLY_KM);
  if (max === Infinity) {
    return { kind: 'open', min: null, max: null, text: `עם ${d} ימי ריצה אין מגבלת נפח עליונה` };
  }
  // The marathon's own availability (minimum volume and day count) is decided by the server at save
  // (schemas.marathon_availability) — say so instead of implying the matrix span is the whole answer.
  const serverNote = marathon ? ' · המינימום וימי הריצה למרתון נבדקים בשמירה' : '';
  const capped = marathon && max === MARATHON_MAX_WEEKLY_KM;
  const next = capped ? null : KM_DAYS_MIN_DAYS.find(([upper]) => upper > max);
  const nextText = next ? ` · מעל ${max} ק״מ נדרשים לפחות ${next[1]} ימים` : '';
  return { kind: 'max', min: null, max,
           text: `עם ${d} ימי ריצה: עד ${max} ק״מ בשבוע${capped ? ' (התקרה למרתון)' : ''}${nextText}${serverNote}` };
}
